import { useState } from "react";

import { useFormContext } from "react-hook-form";

import CrossIcon from "@/images/cross.svg?react";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import Group from "@/tpo/Group";
import InputWrapper from "@/tpo/InputWrapper";
import Menu, { Button } from "@/tpo/Menu";
import Stack from "@/tpo/Stack";
import ButtonV2 from "@/v2/Buttons";

import {
  practitionerApplicationDropdownButtonSx,
  practitionerApplicationDropdownWrapperProps
} from "./practitionerApplicationDropdownLayout";

function findProfessionalBody(professionalBodies, membershipId) {
  return professionalBodies.find(({ id }) => String(id) === String(membershipId));
}

export default function ProfessionalBodyField({
  professionalBodies,
  onSelect,
  onRemove,
  onRegistrationNumberChange
}) {
  const { watch, getFieldState } = useFormContext();
  const selectedMemberships = watch("professionalBodies") || [];
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  const bodiesError = getFieldState("professionalBodies").error;
  const membershipsError = getFieldState("professionalBodyMemberships").error;
  const error = bodiesError || membershipsError;

  const selectedIds = new Set(selectedMemberships.map(membership => String(membership.id)));

  return (
    <Stack alignItems="flex-start" gap={[20, 20, 40]} width="100%">
      <InputWrapper
        {...practitionerApplicationDropdownWrapperProps}
        error={error?.message}
        input={
          <Menu opened={showFilterMenu} onChange={setShowFilterMenu} trigger="hover">
            <Menu.Target>
              <Button
                fullWidth
                open={showFilterMenu}
                data-component-name="professionalBody"
                sx={practitionerApplicationDropdownButtonSx}
              >
                Select options
              </Button>
            </Menu.Target>
            <Menu.Dropdown
              mt={2}
              py={2}
              zIndex={2}
              minWidth={200}
              height={400}
              maxHeight={400}
              overflowY="auto"
            >
              {professionalBodies.map(body => (
                <Menu.Item
                  onClick={() => {
                    if (!selectedIds.has(String(body.id))) {
                      onSelect(body.id);
                    }
                    setShowFilterMenu(false);
                  }}
                  selected={selectedIds.has(String(body.id))}
                  key={body.id}
                  fontSize={14}
                  lineHeight={1.7}
                  py={16}
                  data-testid={`professionalBodyOption:${body.name}`}
                >
                  {body.name}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        }
      />
      {!!selectedMemberships.length && (
        <Group gap={10} flexWrap="wrap">
          {selectedMemberships.map(membership => {
            const body = findProfessionalBody(professionalBodies, membership.id);
            if (!body) {
              return null;
            }

            return (
              <ButtonV2
                key={String(membership.id)}
                leftIcon={<CrossIcon fill="white" width={10} />}
                variant="pill"
                size={["xs", "xs", "sm"]}
                type="button"
                sx={{
                  color: "white",
                  bg: "green",
                  borderStyle: "solid",
                  borderWidth: 0,
                  borderColor: "white",
                  gap: 10
                }}
                onClick={() => onRemove(membership.id)}
                data-component-name="professionalBodyValue"
                data-testid="professionalBodyValue"
              >
                {body.name}
              </ButtonV2>
            );
          })}
        </Group>
      )}
      {selectedMemberships.map(membership => {
        const body = findProfessionalBody(professionalBodies, membership.id);
        if (!body) {
          return null;
        }

        const label = body.requiresRegistrationNumber
          ? body.registrationNumberLabel || "Registration number"
          : body.registrationNumberLabel
          ? `${body.registrationNumberLabel} (optional)`
          : `Membership number — ${body.name} (optional)`;

        return (
          <FloatingLabelInput
            key={`registration-${String(membership.id)}`}
            width="100%"
            label={label}
            value={membership.registrationNumber || ""}
            onChange={event => onRegistrationNumberChange(membership.id, event.target.value)}
            name={`professionalBodyRegistration-${String(membership.id)}`}
          />
        );
      })}
    </Stack>
  );
}
